import { OnlyOnPrint, PrintAvoidBreak, PrintBreakAfter, PrintStyle } from "../designSystem/compositeComponents/Print";
import { IndicatorReport, QuestionStats, SelectionSummary, Survey, SurveySegment, ThemeReport, ThemeReportData, surveyHelper } from "../amorg-common";
import { H1, H2, H3 } from "../designSystem/typography/Heading";
import { Small } from "../designSystem/typography/Body";
import { Flex, FlexItem } from "../designSystem/compositeComponents/FlexHelpers";
import { ReportSummary } from "../features/surveys/report/ReportSummary";
import React from "react";
import { AnswerRatioBarGraph } from "../features/surveys/report/AnswerRatioBarGraph";
import { RadialPctChart } from "../designSystem/graphs/RadialPctChart";
import { AnswerRatioVerticalGraph } from "../features/surveys/report/AnswerRatioVerticalGraph";
import styled from "styled-components";
import { PublicPageProvider } from "./_contexts/PublicPageContext";


type PrintSurveyReportProps = {
    survey: Survey,
    questionStats: QuestionStats[],
    segmentFilter: SurveySegment[][],
    innerRef: React.Ref<HTMLDivElement>
}

export default function PrintSurveyReportPage(props: PrintSurveyReportProps): JSX.Element 
{
    const [themes, setThemes] = React.useState<ThemeReportData[]>([]);
    const [indicatorReport, setIndicatorReport] = React.useState<IndicatorReport | null>(null);
    const [allThemedQuestions, setAllThemedQuestions] = React.useState([]);

    React.useEffect(() => {
        setThemes(
            new ThemeReport(props.survey, props.questionStats).data
        );
    }, [props.questionStats])

    const allThemeQuestions = surveyHelper.getAllSurveyQuestions(props.survey);

    
    React.useEffect(() => {
        setAllThemedQuestions(themes.flatMap(t => t.questionData));
    }, [themes]);

    React.useEffect(() => {
        allThemedQuestions.length > 0 &&
        setIndicatorReport(new IndicatorReport(props.survey, allThemedQuestions.map(q => q.questionReport)));
    }, [allThemedQuestions]);
    
    

    return (
        <PublicPageProvider>
        <OnlyOnPrint>
            <PrintStyle ref={props.innerRef}>
                <Flex flexDirection="column" gap={16}>
                    <H1>{props.survey.title}</H1>
                    <PrintBreakAfter>
                        <Flex flexDirection="column" gap={32}>
                            <Flex flexDirection="column" gap={4}>
                                <H2>Segmenter</H2>
                                <Flex flexDirection="row" gap={16} flexWrap="wrap">
                                    {props.segmentFilter.map( (segment, i) => (
                                        <FlexItem key={i}>
                                            {segment.map(s => s.value).join(", ")}
                                        </FlexItem>
                                    ))}
                                </Flex>
                            </Flex>

                            <Flex flexDirection="column" gap={4}>
                                <H2>Overblik</H2>
                                <Flex flexDirection="row" gap={16} flexWrap="wrap">
                                    <ReportSummary survey={props.survey} questionReports={props.questionStats} />
                                </Flex>
                            </Flex>

                            <PrintAvoidBreak>
                                <Flex flexDirection="column" gap={4}>
                                    <H2>Indikatorer</H2>
                                    <Flex flexDirection="row" gap={16} flexWrap="wrap">
                                        {indicatorReport && indicatorReport.data.map(ir => 
                                            <Flex flexDirection="row" gap={48} key={ir.indicator}>
                                                <Flex flexDirection="row" alignItems="center" width="15%">
                                                    <RadialPctChart dim="120px" fontSize="9px" pct={ir.selections.agreePlusPct} />        
                                                </Flex>
                                                <Flex flexDirection="column" width="85%">
                                                    <H3>{ir.indicator}</H3>
                                                    <Small>
                                                        Indikatoren er anvendt på {allThemedQuestions.filter(q => q.surveyQuestion.indicators.includes(ir.indicator)).length} spørgsmål,
                                                        der i alt er blevet besvaret {ir.selections.totalSelections} gange.
                                                    </Small>
                                                    <AnswerRatioVerticalGraph summaryStats={ir.selections} />
                                                </Flex>
                                            </Flex>
                                            )}
                                    </Flex>
                                </Flex>
                            </PrintAvoidBreak>

                        </Flex>
                    </PrintBreakAfter>
                    
                    <H2>Spørgsmål fordelt på tema</H2>
                    {themes.map((theme, index) => {
                        return (
                            <PrintBreakAfter key={index}>
                                <Flex flexDirection="column" gap={16}>
                                    
                                    <Flex flexDirection="row" gap={48} alignItems="flex-start">
                                        <Flex flexDirection="row" alignItems="center" width="15%">
                                            <RadialPctChart dim="90px" fontSize="9px" pct={theme.selections.agreePlusPct} />
                                        </Flex>
                                        <Flex flexDirection="column" width="85%">
                                            <H3>{theme.theme}</H3>
                                            <Small>
                                                Temaet indholder {theme.questionData.length} spørgsmål, der i alt er blevet besvaret {theme.selections.totalSelections} gange.
                                            </Small>
                                            <AnswerRatioVerticalGraph summaryStats={theme.selections} />
                                        </Flex>
                                    </Flex>

                                    <Flex flexDirection="column" gap={8}>
                                        {theme.questionData.map( (question, index) => {

                                        const originalQuestionIndex = allThemeQuestions.findIndex(o => o.question.title === question.surveyQuestion.title);
                                        const data:SelectionSummary = question.questionReport.selections.data;

                                        return (
                                            <PrintAvoidBreak key={index}>
                                                <Flex flexDirection="row" gap={48} justifyContent="space-between">
                                                    <Flex flexDirection="row" alignItems="center" width="15%">
                                                        <QuestionNumber>#{originalQuestionIndex + 1}</QuestionNumber>
                                                        <RadialPctChart dim="60px" fontSize="10" pct={question.questionReport.selections.agreePlusPct} />
                                                    </Flex>    
                                                    <Flex flexDirection="column" gap={8} alignItems='flex-start' width="85%">
                                                        
                                                        <QuestionTitle>{question.surveyQuestion.title}</QuestionTitle>
                                                        <FlexItem width="100%">
                                                            <AnswerRatioBarGraph data={data} legend="none" showValueText />
                                                        </FlexItem>
                                                    </Flex>
                                                </Flex>
                                            </PrintAvoidBreak>
                                        );
                                        })}
                                    </Flex>
                                </Flex>
                            </PrintBreakAfter>
                        );
                    })}
                </Flex>
            </PrintStyle>
        </OnlyOnPrint>
        </PublicPageProvider>
    );
}

const QuestionNumber = styled.div`
    color: rgba(191,191,191,1);
    font-size:1rem;
    font-weight: 700;
    line-height: 1.5rem;
    margin-right: 5px;
    flex:0;
`;

const QuestionTitle = styled.div`
    overflow-wrap: break-word;
    white-space: pre-wrap; 
    font-weight: 400;
`;